import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Typography,
    CircularProgress,
    Alert,
    Box,
    Avatar,
    IconButton,
    Divider,
    Switch,
    FormControlLabel,
    InputAdornment
} from '@mui/material';
import { 
    CameraAlt as CameraIcon, 
    Save as SaveIcon, 
    Close as CloseIcon,
    Visibility,
    VisibilityOff
} from '@mui/icons-material';
import apiClient from '../api';
import PasswordValidator from './PasswordValidator';

function UserProfileForm({ open, onClose, onSave, user }) {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
        profilePicture: null,
        darkMode: false,
        emailNotifications: true,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [viewMode, setViewMode] = useState('general'); // 'general', 'security', 'preferences'
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [passwordValidation, setPasswordValidation] = useState({ isValid: false, strength: 0 });
    
    // Only initialize form data when dialog opens - ignore user prop changes while open
    useEffect(() => {
        if (open && user) {
            console.log('[Profile Form] Dialog opened, initializing form data');
            setFormData({
                name: user.name || '',
                email: user.email || '',
                phone: user.phone || '',
                currentPassword: '',
                newPassword: '',
                confirmPassword: '',
                profilePicture: user.profilePicture || null,
                darkMode: user.darkMode || false,
                emailNotifications: user.emailNotifications !== false,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
            });
            // Clear any previous states
            setError(null);
            setSuccessMessage(null);
            setLoading(false);
            setViewMode('general');
            // Reset password visibility
            setShowCurrentPassword(false);
            setShowNewPassword(false);
            setShowConfirmPassword(false);
        }
    }, [open]); // Only depend on open, not user
    
    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };
    
    const handleProfilePictureChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setFormData(prev => ({
                    ...prev,
                    profilePicture: reader.result
                }));
            };
            reader.readAsDataURL(file);
        }
    };
    
    const validateForm = () => {
        // Reset errors
        setError(null);
        
        // Validate passwords match if changing password
        if (formData.newPassword) {
            if (!formData.currentPassword) {
                setError("Current password is required to set a new password");
                return false;
            }
            
            if (formData.newPassword !== formData.confirmPassword) {
                setError("New passwords don't match");
                return false;
            }
            
            // Use the password validator component's validation
            if (!passwordValidation.isValid) {
                setError("Password does not meet security requirements");
                return false;
            }
        }
        
        // Validate email
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            setError("Please enter a valid email address");
            return false;
        }
        
        return true;
    };
    
    const handleSubmit = async (event) => {
        event.preventDefault();
        
        if (!validateForm()) return;
        
        setLoading(true);
        setError(null);
        setSuccessMessage(null);
        
        try {
            // Prepare the data for submission
            const { currentPassword, newPassword, confirmPassword, ...safeData } = formData;
            
            // Handle password fields - include them if either has content for proper validation
            if ((currentPassword && currentPassword.trim()) || (newPassword && newPassword.trim())) {
                safeData.currentPassword = currentPassword;
                safeData.newPassword = newPassword;
            }
            
            console.log('[Profile Form] Submitting form data directly to API');
            
            // Call the API directly instead of going through AuthContext
            const response = await apiClient.patch('/users/profile', safeData);
            
            console.log('[Profile Form] API response:', response.data);
            
            if (response.data.success) {
                setSuccessMessage("Profile updated successfully");
                
                // Update the user in localStorage if provided
                if (response.data.data?.user) {
                    const updatedUser = response.data.data.user;
                    localStorage.setItem('authUser', JSON.stringify(updatedUser));
                }
                
                // Clear password fields on success
                setFormData(prev => ({
                    ...prev,
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                }));
                
                // Close the modal after a brief delay to show success message
                setTimeout(() => {
                    onClose();
                }, 1500);
                
            } else {
                setError(response.data.message || "Failed to update profile");
            }
        } catch (err) {
            console.error('Error updating profile:', err);
            const errorMessage = err.response?.data?.message || err.message || "Failed to update profile";
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };
    
    const handleCancel = () => {
        console.log('[Profile Form] handleCancel called - closing modal');
        onClose();
    };
    
    return (
        <Dialog 
            open={open} 
            onClose={loading ? undefined : (event, reason) => {
                console.log('[Profile Form] Dialog onClose triggered, reason:', reason);
                onClose();
            }} 
            maxWidth="md" 
            fullWidth
            scroll="paper"
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h5">Edit Profile</Typography>
                    <IconButton onClick={handleCancel} disabled={loading} edge="end">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            
            <form onSubmit={handleSubmit}>
                <DialogContent dividers sx={{ maxHeight: 'calc(100vh - 220px)', overflowY: 'auto', pb: 8 }}>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}
                    
                    {successMessage && (
                        <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>
                    )}
                    
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                            {/* Navigation sidebar */}
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Button 
                                    variant={viewMode === 'general' ? 'contained' : 'text'} 
                                    onClick={() => setViewMode('general')}
                                    sx={{ justifyContent: 'flex-start', mb: 1 }}
                                >
                                    General Information
                                </Button>
                                <Button 
                                    variant={viewMode === 'security' ? 'contained' : 'text'} 
                                    onClick={() => setViewMode('security')}
                                    sx={{ justifyContent: 'flex-start', mb: 1 }}
                                >
                                    Security
                                </Button>
                                <Button 
                                    variant={viewMode === 'preferences' ? 'contained' : 'text'} 
                                    onClick={() => setViewMode('preferences')}
                                    sx={{ justifyContent: 'flex-start', mb: 1 }}
                                >
                                    Preferences
                                </Button>
                            </Box>
                        </Grid>
                        
                        <Grid item xs={12} md={9}>
                            {/* Form content based on view mode */}
                            {viewMode === 'general' && (
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                        <Box sx={{ position: 'relative' }}>
                                            <Avatar 
                                                src={formData.profilePicture} 
                                                sx={{ 
                                                    width: 120, 
                                                    height: 120,
                                                    boxShadow: 3
                                                }}
                                            >
                                                {!formData.profilePicture && formData.name.charAt(0).toUpperCase()}
                                            </Avatar>
                                            <input
                                                accept="image/*"
                                                type="file"
                                                id="profile-picture-input"
                                                onChange={handleProfilePictureChange}
                                                style={{ display: 'none' }}
                                            />
                                            <label htmlFor="profile-picture-input">
                                                <IconButton 
                                                    component="span"
                                                    sx={{ 
                                                        position: 'absolute',
                                                        bottom: 0,
                                                        right: 0,
                                                        backgroundColor: 'primary.main',
                                                        color: 'white',
                                                        '&:hover': {
                                                            backgroundColor: 'primary.dark'
                                                        }
                                                    }}
                                                >
                                                    <CameraIcon fontSize="small" />
                                                </IconButton>
                                            </label>
                                        </Box>
                                    </Grid>
                                    
                                    <Grid item xs={12}>
                                        <TextField
                                            name="name"
                                            label="Full Name"
                                            fullWidth
                                            variant="outlined"
                                            value={formData.name}
                                            onChange={handleChange}
                                            required
                                        />
                                    </Grid>
                                    
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            name="email"
                                            label="Email Address"
                                            fullWidth
                                            variant="outlined"
                                            value={formData.email}
                                            onChange={handleChange}
                                            required
                                            type="email"
                                        />
                                    </Grid>
                                    
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            name="phone"
                                            label="Phone Number"
                                            fullWidth
                                            variant="outlined"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            placeholder="(123) 456-7890"
                                        />
                                    </Grid>
                                </Grid>
                            )}
                            
                            {viewMode === 'security' && (
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle1" gutterBottom>
                                            Change Password
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" paragraph>
                                            Leave the fields blank if you don't want to change your password.
                                        </Typography>
                                    </Grid>
                                    
                                    <Grid item xs={12}>
                                        <TextField
                                            name="currentPassword"
                                            label="Current Password"
                                            fullWidth
                                            variant="outlined"
                                            value={formData.currentPassword}
                                            onChange={handleChange}
                                            type={showCurrentPassword ? 'text' : 'password'}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            aria-label="toggle password visibility"
                                                            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                                            edge="end"
                                                        >
                                                            {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                    
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            name="newPassword"
                                            label="New Password"
                                            fullWidth
                                            variant="outlined"
                                            value={formData.newPassword}
                                            onChange={handleChange}
                                            type={showNewPassword ? 'text' : 'password'}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            aria-label="toggle password visibility"
                                                            onClick={() => setShowNewPassword(!showNewPassword)}
                                                            edge="end"
                                                        >
                                                            {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                    
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            name="confirmPassword"
                                            label="Confirm New Password"
                                            fullWidth
                                            variant="outlined"
                                            value={formData.confirmPassword}
                                            onChange={handleChange}
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            aria-label="toggle password visibility"
                                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                            edge="end"
                                                        >
                                                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                    
                                    {formData.newPassword && (
                                        <Grid item xs={12}>
                                            <Box sx={{ mt: 2 }}>
                                                <PasswordValidator 
                                                    password={formData.newPassword}
                                                    onValidationChange={setPasswordValidation}
                                                />
                                            </Box>
                                        </Grid>
                                    )}
                                </Grid>
                            )}
                            
                            {viewMode === 'preferences' && (
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle1" gutterBottom>
                                            Display & Notifications
                                        </Typography>
                                    </Grid>
                                    
                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={formData.darkMode}
                                                    onChange={handleChange}
                                                    name="darkMode"
                                                    color="primary"
                                                />
                                            }
                                            label="Dark Mode"
                                        />
                                    </Grid>
                                    
                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={formData.emailNotifications}
                                                    onChange={handleChange}
                                                    name="emailNotifications"
                                                    color="primary"
                                                />
                                            }
                                            label="Email Notifications"
                                        />
                                    </Grid>
                                    
                                    <Grid item xs={12}>
                                        <TextField
                                            select
                                            name="timezone"
                                            label="Timezone"
                                            fullWidth
                                            variant="outlined"
                                            value={formData.timezone}
                                            onChange={handleChange}
                                            SelectProps={{
                                                native: true,
                                            }}
                                        >
                                            <option value="America/New_York">Eastern Time (ET)</option>
                                            <option value="America/Chicago">Central Time (CT)</option>
                                            <option value="America/Denver">Mountain Time (MT)</option>
                                            <option value="America/Los_Angeles">Pacific Time (PT)</option>
                                            <option value="America/Anchorage">Alaska Time (AKT)</option>
                                            <option value="Pacific/Honolulu">Hawaii Time (HT)</option>
                                        </TextField>
                                    </Grid>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                </DialogContent>
                
                <DialogActions sx={{ px: 3, py: 2, position: 'sticky', bottom: 0, bgcolor: 'background.paper' }}>
                    <Button 
                        onClick={handleCancel} 
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button 
                        type="submit" 
                        variant="contained" 
                        color="primary" 
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                    >
                        {loading ? 'Saving...' : 'Save Changes'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}

export default UserProfileForm; 